import { useState } from "react";
import {
  Box, Button, Typography, IconButton, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";

export default function DocumentCard({ doc, isVisible, onDelete, onCancel, onRetry, getFileContentUrl }) {
  const isUploading = doc.status === "uploading";
  const isError = doc.status === "error";

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteClick = () => setConfirmOpen(true);
  const handleCancel = () => setConfirmOpen(false);
  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    setDeleting(true);
    try {
      await onDelete?.(doc);
    } finally {
      setDeleting(false);
    }
  };

  const viewUrl = doc.path && typeof getFileContentUrl === "function"
    ? getFileContentUrl(doc.path)
    : doc.blobUrl || doc.url;

  const handleView = () => {
    const url = viewUrl;
    const fileName = doc.name?.toLowerCase();

    // PDFs open natively in browser — no change needed
    if (fileName?.endsWith(".pdf")) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    // For PPTX / XLSX — use Microsoft Office Online viewer
    // Only works for publicly accessible URLs (not blob URLs)
    // For local blobs, we fall back to a new tab with an object tag
    if (doc.blobUrl) {
      // Local file — open in a new tab with embedded object viewer
      const html = `
      <html>
        <head><title>${doc.name}</title></head>
        <body style="margin:0;padding:0;background:#525659;">
          <object
            data="${url}"
            type="${getMimeType(doc.name)}"
            width="100%"
            height="100%"
            style="position:absolute;top:0;left:0;width:100%;height:100%;"
          >
            <p style="color:white;text-align:center;padding-top:40px;">
              Preview not available for this file type. 
              <a href="${url}" download="${doc.name}" style="color:#90caf9;">Download instead</a>
            </p>
          </object>
        </body>
      </html>
    `;
      const blob = new Blob([html], { type: "text/html" });
      const viewUrl = URL.createObjectURL(blob);
      window.open(viewUrl, "_blank", "noopener,noreferrer");
      return;
    }

    // For real backend URLs (once API is ready) — use Office Online viewer
    const encoded = encodeURIComponent(url);
    window.open(
      `https://view.officeapps.live.com/op/view.aspx?src=${encoded}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  // Helper to get MIME type from filename
  const getMimeType = (fileName) => {
    const ext = fileName?.split(".").pop()?.toLowerCase();
    const mimeMap = {
      pdf: "application/pdf",
      pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      txt: "text/plain",
    };
    return mimeMap[ext] || "application/octet-stream";
  };

  return (
    <>
      <Box
        sx={{
          width: "45%",
          border: "1px solid",
          borderColor: isError ? "#ef4444" : "#e5e7eb",
          borderRadius: 2,
          p: 1.5,
          bgcolor: "#fff",
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <Typography fontSize={13} fontWeight={500} sx={{ flex: 1 }} noWrap>
            {doc.name}
          </Typography>

          {!isUploading && (
            <>
              <Button
                size="small"
                variant="outlined"
                onClick={() => { if (viewUrl) window.open(viewUrl, "_blank", "noopener,noreferrer"); else handleView(); }}
                sx={{
                  textTransform: "none", fontSize: 12, minWidth: "auto",
                  px: 1.5, height: 26, color: "black",
                  border: "1px solid lightgray", borderRadius: "5px",
                }}
              >
                View
              </Button>

              {isVisible && !deleting && (
                <IconButton size="small" onClick={handleDeleteClick}>
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </IconButton>
              )}

              {deleting && (
                <CircularProgress size={20} />
              )}
            </>
          )}

          {isUploading && (
            <IconButton size="small" onClick={onCancel}>
              <CloseOutlinedIcon fontSize="small" />
            </IconButton>
          )}


        </Box>

        {doc.uploadedAt && (
          <Typography fontSize={11} color="text.secondary" sx={{ mt: 0.5 }}>
            Uploaded on {doc.uploadedAt}
          </Typography>
        )}

        {isUploading && (
          <LinearProgress variant="determinate" value={doc.progress} sx={{ mt: 1 }} />
        )}
      </Box>
      {doc.status === "error" && (
        <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.5}>
          <Typography fontSize={11} color="#ef4444">
            Upload failed. Please try again.
          </Typography>
          <Button
            size="small"
            onClick={onRetry}
            sx={{
              fontSize: 11,
              textTransform: "none",
              color: "#1d4ed8",
              p: 0,
              minWidth: "auto",
              "&:hover": { background: "none", textDecoration: "underline" },
            }}
          >
            Retry
          </Button>
        </Box>
      )}

      {/* ← ADD THIS DIALOG */}
      <Dialog
        open={confirmOpen}
        onClose={handleCancel}
        PaperProps={{ sx: { borderRadius: "12px", p: 1, maxWidth: 380 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16, pb: 1 }}>
          Delete Document
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          <Typography fontSize={14} color="text.secondary">
            Are you sure you want to delete{" "}
            <strong>{doc.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleCancel}
            variant="outlined"
            sx={{ textTransform: "none", borderRadius: "8px", color: "black", borderColor: "#e5e7eb" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{ textTransform: "none", borderRadius: "8px", bgcolor: "#111827", "&:hover": { bgcolor: "#374151" } }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}